import { useEffect, type RefObject } from 'react';

/**
 * Shared HLS playback hook used by both `<StreamingVideo>` (resurs/streaming-video)
 * and `<VideoSection>` (resurs/video-section, when an HLS source is supplied).
 *
 * Responsibilities:
 *   - Decide whether the browser can play HLS natively (true Safari only —
 *     Chrome reports `'maybe'` for `application/vnd.apple.mpegurl` but its
 *     native HLS implementation is broken and exposes no API for variant
 *     selection).
 *   - For non-Safari browsers, dynamically `import()` hls.js so the ~80 KB
 *     gzipped bundle is only loaded on pages that actually use HLS.
 *   - Configure ABR so the initial quality is chosen from a real bandwidth
 *     measurement (`startLevel: -1`) and so a level whose resolution exceeds
 *     the rendered player size is never selected (`capLevelToPlayerSize`).
 *     The hook intentionally does NOT pin `currentLevel`/`loadLevel` — doing
 *     so disables ABR for subsequent fragments and prevents downshifting
 *     when bandwidth drops, which is what caused throttled clients to
 *     thrash between a brief 360p start and a stuttering 1080p.
 *   - Trigger `play()` on `MANIFEST_PARSED` when `autoplay` is requested.
 *     The HTML `autoplay` attribute alone isn't enough on Chrome/Firefox —
 *     by the time hls.js attaches the MediaSource, the browser has already
 *     decided "no playable source" and won't auto-restart.
 *
 * Config is limited to the three ABR options above; everything else is an
 * hls.js default. To start at top quality instead, set
 * `instance.currentLevel = instance.levels.length - 1` inside the
 * MANIFEST_PARSED handler — but see the downshifting caveat above first.
 *
 * Caller responsibilities:
 *   - Render `<source src={masterUrl} type="application/vnd.apple.mpegurl">`
 *     inside the `<video>` so Safari's native HLS path works without any JS.
 *   - Set the HTML `autoplay` and `muted` attributes on the `<video>` (the
 *     hook only handles the programmatic-play call for non-Safari paths).
 *   - Keep CSP `media-src` permissive enough to allow `blob:` URLs — hls.js
 *     attaches video data via a blob: MediaSource URL.
 */
export type UseHlsPlaybackOptions = {
    masterUrl?: string;
    autoplay?: boolean;
};

export const useHlsPlayback = (videoRef: RefObject<HTMLVideoElement | null>, { masterUrl, autoplay = false }: UseHlsPlaybackOptions) => {
    useEffect(() => {
        // Bail in SSR (no DOM yet) or when no source/ref is available. The
        // cleanup function below handles cases where masterUrl changes
        // between renders.
        if (typeof window === 'undefined') return undefined;
        if (!masterUrl) return undefined;
        const videoEl = videoRef.current;
        if (!videoEl) return undefined;

        // Native HLS path: only trust real Safari. Chrome 147+ on Linux
        // returns canPlayType('application/vnd.apple.mpegurl') = 'maybe',
        // but its native HLS is inconsistent and locks at low quality
        // with no JS API to pick a variant. Sniff the UA to be sure:
        // Safari has "Safari" in the UA but Chrome/Edge inherit it, so
        // exclude those explicitly.
        const ua = navigator.userAgent;
        const isSafari = /Safari/.test(ua) && !/Chrome|Chromium|Edg\//.test(ua);
        const canPlayNative = isSafari && videoEl.canPlayType('application/vnd.apple.mpegurl');
        if (canPlayNative) {
            return undefined;
        }

        let cancelled = false;
        let hls: { destroy: () => void } | null = null;

        (async () => {
            try {
                const HlsMod = await import('hls.js');
                if (cancelled) return;
                const Hls = HlsMod.default;

                // Ancient browser without MSE — give up silently. The
                // native <source> attempt has already played out; the
                // <video controls> shows its broken-playback state.
                if (!Hls.isSupported()) return;

                const instance = new Hls({
                    // Use measured per-level bitrate instead of trusting the
                    // manifest's BANDWIDTH attribute. Our IA3K-generated
                    // master playlists declare BANDWIDTH ~12-26% above the
                    // actual encoded bitrate (normal HLS behavior — peak +
                    // container overhead). With the default `false`, ABR
                    // demands `1.43 × manifestBitrate` headroom and can get
                    // stuck one rung below what the connection can actually
                    // sustain. With `true`, ABR uses what it actually
                    // measured downloading the level, matching reality.
                    abrMaxWithRealBitrate: true,
                    // Cap the auto-selected quality level to the rendered
                    // size of the <video> element (× devicePixelRatio).
                    // A 600px-wide player on a 1× display won't fetch 1080p
                    // — it'd be wasted bytes the screen can't show. When
                    // the user goes fullscreen or the page layout grows
                    // the player, hls.js re-evaluates the cap and uses
                    // `nextLevel` semantics to upgrade the forward buffer
                    // to higher quality (currently-playing fragment is
                    // untouched, so no visible playback hitch — visible in
                    // the trace as `switching → #N` followed by re-fetching
                    // already-buffered fragments at the new level).
                    capLevelToPlayerSize: true,
                    // Auto-probe startup: fetch the lowest-bitrate variant
                    // first as a quick bandwidth probe, then let ABR pick
                    // the appropriate level for fragment #1 onwards based
                    // on the measurement. Predictable startup latency
                    // (small probe fragment) and accurate first real level
                    // pick — no reliance on the manifest-first-variant
                    // seed-clamping heuristic that otherwise lands us at
                    // 720p when the connection could handle 1080p.
                    startLevel: -1,
                });

                // Register MANIFEST_PARSED BEFORE loadSource — if the
                // master is cached and parses synchronously we'd miss
                // the event with the listener attached afterwards.
                instance.on(Hls.Events.MANIFEST_PARSED, () => {
                    if (autoplay) {
                        videoEl.play().catch(() => {
                            // Some user agents reject programmatic
                            // play() even on muted videos until first
                            // interaction. The visible <video controls>
                            // (or a custom control overlay) lets the
                            // visitor tap to start.
                        });
                    }
                });

                // Debug logging: enable with ?hls-debug=1 in the URL.
                // Useful for confirming what variant is actually playing
                // and watching ABR react to bandwidth changes / seeks.
                const debug = new URLSearchParams(window.location.search).has('hls-debug');
                if (debug) {
                    /* eslint-disable no-console */
                    const fmtLevel = (i: number) => {
                        const lvl = instance.levels[i];
                        if (!lvl) return `#${i} (?)`;
                        const kbps = Math.round((lvl.bitrate || 0) / 1000);
                        return `#${i} ${lvl.height || '?'}p @ ${kbps}kbps`;
                    };
                    const bufferAhead = () => {
                        const t = videoEl.currentTime;
                        const buf = videoEl.buffered;
                        for (let i = 0; i < buf.length; i++) {
                            if (t >= buf.start(i) && t <= buf.end(i)) {
                                return (buf.end(i) - t).toFixed(1);
                            }
                        }
                        return '0.0';
                    };
                    const bwEstKbps = () => Math.round((instance.bandwidthEstimate || 0) / 1000);

                    // ── Manifest & levels ──────────────────────────────
                    instance.on(Hls.Events.MANIFEST_PARSED, (_e, data) => {
                        console.info(
                            '[hls] manifest parsed — levels:',
                            data.levels.map((_l, i) => fmtLevel(i))
                        );
                    });
                    instance.on(Hls.Events.LEVELS_UPDATED, (_e, data) => {
                        console.info('[hls] levels updated — remaining:', data.levels.length);
                    });

                    // ── ABR level switches ─────────────────────────────
                    instance.on(Hls.Events.LEVEL_SWITCHING, (_e, data) => {
                        console.info(`[hls] switching → ${fmtLevel(data.level)} (bw est ${bwEstKbps()}kbps)`);
                    });
                    instance.on(Hls.Events.LEVEL_SWITCHED, (_e, data) => {
                        console.info(`[hls] now playing → ${fmtLevel(data.level)}`);
                    });

                    // ── Fragment lifecycle ─────────────────────────────
                    instance.on(Hls.Events.FRAG_LOADING, (_e, data) => {
                        console.debug(`[hls] frag #${data.frag.sn} loading (level ${fmtLevel(data.frag.level)})`);
                    });
                    instance.on(Hls.Events.FRAG_LOADED, (_e, data) => {
                        const stats = data.frag.stats;
                        const bytes = stats.total || 0;
                        const loadMs = stats.loading?.end - stats.loading?.start || 0;
                        const throughputKbps = loadMs > 0 ? Math.round((bytes * 8) / loadMs) : 0;
                        console.debug(
                            `[hls] frag #${data.frag.sn} loaded — ${(bytes / 1024).toFixed(0)}KB in ${loadMs}ms ` +
                                `(${throughputKbps}kbps), bw est ${bwEstKbps()}kbps, buffer ahead ${bufferAhead()}s`
                        );
                    });
                    instance.on(Hls.Events.FRAG_CHANGED, (_e, data) => {
                        console.info(
                            `[hls] frag #${data.frag.sn} now playing (level ${fmtLevel(data.frag.level)}) — ` +
                                `bw est ${bwEstKbps()}kbps, buffer ahead ${bufferAhead()}s`
                        );
                    });

                    // ── Errors (including non-fatal) ──────────────────
                    instance.on(Hls.Events.ERROR, (_e, data) => {
                        const tag = data.fatal ? '[hls] FATAL' : '[hls] error';
                        console.warn(`${tag} ${data.type}/${data.details}`, data);
                    });

                    // ── Native <video> playback signals ────────────────
                    const onSeeking = () => console.info(`[hls] seeking → ${videoEl.currentTime.toFixed(1)}s`);
                    const onSeeked = () => console.info(`[hls] seeked → ${videoEl.currentTime.toFixed(1)}s (buffer ahead ${bufferAhead()}s)`);
                    const onPlay = () => console.info('[hls] play');
                    const onPause = () => console.info('[hls] pause');
                    const onWaiting = () =>
                        console.warn(`[hls] WAITING (stall) at ${videoEl.currentTime.toFixed(1)}s — buffer ahead ${bufferAhead()}s`);
                    const onStalled = () => console.warn('[hls] stalled (network)');
                    const onRateChange = () => console.info(`[hls] playbackRate → ${videoEl.playbackRate}`);
                    videoEl.addEventListener('seeking', onSeeking);
                    videoEl.addEventListener('seeked', onSeeked);
                    videoEl.addEventListener('play', onPlay);
                    videoEl.addEventListener('pause', onPause);
                    videoEl.addEventListener('waiting', onWaiting);
                    videoEl.addEventListener('stalled', onStalled);
                    videoEl.addEventListener('ratechange', onRateChange);
                    /* eslint-enable no-console */
                }

                instance.on(Hls.Events.ERROR, (_event, data) => {
                    if (!data.fatal) return;
                    switch (data.type) {
                        case Hls.ErrorTypes.NETWORK_ERROR:
                            instance.startLoad();
                            break;
                        case Hls.ErrorTypes.MEDIA_ERROR:
                            instance.recoverMediaError();
                            break;
                        default:
                            instance.destroy();
                            break;
                    }
                });

                instance.loadSource(masterUrl);
                instance.attachMedia(videoEl);

                hls = instance;
            } catch {
                // Dynamic import failed (offline, bundler error, CSP
                // block on the JS chunk). Nothing actionable; the
                // <source> already failed too on this branch.
            }
        })();

        return () => {
            cancelled = true;
            if (hls) {
                hls.destroy();
            }
        };
    }, [videoRef, masterUrl, autoplay]);
};
